import { ReactNode, useEffect } from "react";

import { css } from "@compiled/react";

import Form from "@atlaskit/form";

import { experiments, useExperiment } from "@/utils/experiment";

import { useSiteActivationContext } from "../../hooks/site-activation-context";
import {
    SiteNameInput as DefaultSiteNameInput,
    SitePicker as DefaultSitePicker,
    SiteToggleButton as DefaultSiteToggleButton
} from "../common";
import { TransactionAccountSelector } from "../transaction-account-selector/transaction-account-selector";

import { ExistingSitesSubmitButton as DefaultSubmitButton } from "./submit-button";

export const Content = ({
    SitePicker = <DefaultSitePicker />,
    SiteNameInput = <DefaultSiteNameInput />,
    SiteToggleButton = <DefaultSiteToggleButton />,
    SubmitButton,
    PrivacyPolicy = null
}: {
    SitePicker?: ReactNode;
    SiteNameInput?: ReactNode;
    SiteToggleButton?: ReactNode;
    SubmitButton?: ReactNode;
    PrivacyPolicy?: ReactNode;
}) => {
    const { showSitePicker, setShowSitePicker, activateSite, sites } =
        useSiteActivationContext();

    const sitePickerEnabled =
        sites.length > 0 && SitePicker && SiteToggleButton;

    // if the site picker is not enabled, let context know it is not showing
    useEffect(() => {
        if (!sitePickerEnabled && showSitePicker) {
            setShowSitePicker(false);
        }
    }, [sitePickerEnabled]);

    const activateSiteWithPrivacyPolicy = () => {
        const privacyPolicy =
            document.getElementById("privacy-policy-text")?.innerHTML || "";
        activateSite({ privacyPolicy });
    };

    if (SubmitButton === undefined) {
        SubmitButton = (
            <DefaultSubmitButton hasPrivacyPolicy={!!PrivacyPolicy} />
        );
    }

    // Only show if more than one transaction account, creating a new site, and experiment is enabled
    const gwp8627SignupTransactionSelectorExperiment = useExperiment(
        experiments.gwp8627SignupTransactionSelector
    );
    const showTxaSelector =
        !showSitePicker &&
        gwp8627SignupTransactionSelectorExperiment &&
        gwp8627SignupTransactionSelectorExperiment.type === "enrolled" &&
        gwp8627SignupTransactionSelectorExperiment.value;

    return (
        <Form onSubmit={activateSiteWithPrivacyPolicy} isDisabled={false}>
            {({ formProps }) => (
                <form
                    {...formProps}
                    autoComplete="off"
                    css={containerCss}
                    data-testid="existing-sites-site-activation"
                >
                    <div>
                        {showSitePicker && sitePickerEnabled
                            ? SitePicker
                            : SiteNameInput}
                        {sitePickerEnabled && SiteToggleButton}
                    </div>
                    {showTxaSelector && <TransactionAccountSelector />}
                    {SubmitButton}
                    <div id="privacy-policy-text">{PrivacyPolicy}</div>
                </form>
            )}
        </Form>
    );
};

const containerCss = css({
    display: "grid",
    gap: "24px"
});
